import { useState } from 'react';
import { useCreate, useNotify, useRedirect } from 'react-admin';
import {
  Alert,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import { useClaimPolicyUtilization } from '@pumpkincare/claims';

import RoutePaths from '../../../routes';
import { convertDollarsToCents, formatCurrency } from '../../../shared/utils';
import AdjustPolicyUtilizationConfirmationModal from '../adjust-policy-utilization-confirmation-modal';
import { adjustPolicyUtilizationStyle } from './adjust-policy-utilization-style';

function getErrorMessage(error) {
  switch (error?.status) {
    case 404:
      return 'Claim not found.';
    case 409:
      return 'This claim is PEP/Wellness and cannot be adjusted here.';
    case 422:
      return 'This claim already had a previous policy utilization adjustment.';
    default:
      return 'An unexpected error occurred while retrieving this claim.';
  }
}

function getDeductibleAdjustmentError(adjustmentCents, consumedDeductible) {
  if (adjustmentCents < 0) {
    return 'Deductible adjustment cannot be negative.';
  }
  if (adjustmentCents > consumedDeductible) {
    return "Deductible adjustment cannot exceed this claim's Total Applied to Deductible.";
  }
  return null;
}

function getAnnualLimitAdjustmentError(adjustmentCents, consumedAnnualLimit) {
  if (adjustmentCents < 0) {
    return 'Annual limit adjustment cannot be negative.';
  }
  if (adjustmentCents > consumedAnnualLimit) {
    return "Annual limit adjustment cannot exceed this claim's Total Paid.";
  }
  return null;
}

function formatRecalculated(recalculatedValue, totalValue) {
  const total =
    totalValue === -1
      ? 'Unlimited'
      : formatCurrency(totalValue, { areCents: true, hideZero: true });
  return `${formatCurrency(recalculatedValue, {
    areCents: true,
    hideZero: true,
  })} / ${total}`;
}

function buildAdjustmentChanges({
  data,
  calculationResult,
  deductibleAdjustmentCents,
  annualLimitAdjustmentCents,
}) {
  const changes = [];

  if (deductibleAdjustmentCents > 0) {
    changes.push({
      key: 'deductible',
      label: 'Deductible',
      adjustment: formatCurrency(deductibleAdjustmentCents, { areCents: true }),
      current: formatRecalculated(
        data.policy_current_deductible,
        data.policy_total_deductible
      ),
      updated: formatRecalculated(
        calculationResult.recalculatedDeductible,
        data.policy_total_deductible
      ),
    });
  }

  if (annualLimitAdjustmentCents > 0) {
    changes.push({
      key: 'annualLimit',
      label: 'Annual Limit',
      adjustment: formatCurrency(annualLimitAdjustmentCents, { areCents: true }),
      current: formatRecalculated(
        data.policy_current_annual_limit,
        data.policy_total_annual_limit
      ),
      updated: formatRecalculated(
        calculationResult.recalculatedAnnualLimit,
        data.policy_total_annual_limit
      ),
    });
  }

  return changes;
}

function AdjustPolicyUtilization({ petId }) {
  const classes = adjustPolicyUtilizationStyle();
  const notify = useNotify();
  const redirect = useRedirect();
  const [submitAdjustment, { isLoading: isSubmitting }] = useCreate();
  const [claimId, setClaimId] = useState('');
  const [submittedClaimId, setSubmittedClaimId] = useState('');
  const [deductibleAdjustment, setDeductibleAdjustment] = useState(0);
  const [annualLimitAdjustment, setAnnualLimitAdjustment] = useState(0);
  const [deductibleError, setDeductibleError] = useState(null);
  const [annualLimitError, setAnnualLimitError] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const { data, error, isLoading, refetch } =
    useClaimPolicyUtilization(submittedClaimId);

  function handleFindClaim() {
    setSubmittedClaimId(claimId);
    setDeductibleAdjustment(0);
    setAnnualLimitAdjustment(0);
    setDeductibleError(null);
    setAnnualLimitError(null);
    setCalculationResult(null);
  }

  function handleDeductibleAdjustmentChange(event) {
    setDeductibleAdjustment(event.target.value);
    setDeductibleError(null);
    setCalculationResult(null);
  }

  function handleAnnualLimitAdjustmentChange(event) {
    setAnnualLimitAdjustment(event.target.value);
    setAnnualLimitError(null);
    setCalculationResult(null);
  }

  function handleCalculate() {
    const deductibleAdjustmentCents = convertDollarsToCents(deductibleAdjustment);
    const annualLimitAdjustmentCents = convertDollarsToCents(annualLimitAdjustment);

    const deductibleErrorMessage = getDeductibleAdjustmentError(
      deductibleAdjustmentCents,
      data.consumed_deductible
    );
    const annualLimitErrorMessage = getAnnualLimitAdjustmentError(
      annualLimitAdjustmentCents,
      data.consumed_annual_limit
    );

    setDeductibleError(deductibleErrorMessage);
    setAnnualLimitError(annualLimitErrorMessage);

    if (deductibleErrorMessage || annualLimitErrorMessage) {
      setCalculationResult(null);
      return;
    }

    setCalculationResult({
      recalculatedDeductible:
        data.policy_current_deductible - deductibleAdjustmentCents,
      recalculatedAnnualLimit:
        data.policy_current_annual_limit - annualLimitAdjustmentCents,
    });
  }

  function handleApply() {
    setIsConfirmationModalOpen(true);
  }

  function handleCloseConfirmationModal() {
    setIsConfirmationModalOpen(false);
  }

  function handleConfirmAdjustment() {
    submitAdjustment(
      `claims/${submittedClaimId}/policy-utilization-adjustments`,
      {
        data: {
          initial_deductible: data.policy_current_deductible,
          initial_annual_limit: data.policy_current_annual_limit,
          deductible_adjustment: convertDollarsToCents(deductibleAdjustment),
          annual_limit_adjustment: convertDollarsToCents(annualLimitAdjustment),
        },
      },
      {
        onSuccess: () => {
          setIsConfirmationModalOpen(false);
          notify('Policy utilization adjusted successfully.', { type: 'success' });
          redirect(`/${RoutePaths.pets}/${petId}/show`);
        },
        onError: submissionError => {
          setIsConfirmationModalOpen(false);
          notify(submissionError.message, { type: 'error' });
          setCalculationResult(null);
          refetch();
        },
      }
    );
  }

  const isClaimForDifferentPet = !!(data && petId && data.pet_id !== petId);
  const isCalculateEnabled =
    Number(deductibleAdjustment) > 0 || Number(annualLimitAdjustment) > 0;
  const isApplyEnabled = calculationResult !== null;
  const adjustmentChanges = calculationResult
    ? buildAdjustmentChanges({
        data,
        calculationResult,
        deductibleAdjustmentCents: convertDollarsToCents(deductibleAdjustment),
        annualLimitAdjustmentCents: convertDollarsToCents(annualLimitAdjustment),
      })
    : [];

  return (
    <div className={classes.root}>
      <div className={classes.searchRow}>
        <TextField
          variant='outlined'
          label='Claim ID'
          value={claimId}
          onChange={event => setClaimId(event.target.value)}
        />
        <Button variant='contained' disabled={!claimId} onClick={handleFindClaim}>
          Find Claim
        </Button>
      </div>

      {isLoading ? <CircularProgress size={20} /> : null}

      {!isLoading && error ? (
        <Alert severity='error'>{getErrorMessage(error)}</Alert>
      ) : null}

      {!isLoading && data && isClaimForDifferentPet ? (
        <Alert severity='error'>This claim is not associated with this pet.</Alert>
      ) : null}

      {!isLoading && data && !isClaimForDifferentPet ? (
        <>
          {data.policy_has_opened_claims ? (
            <Alert severity='warning'>
              This claim&apos;s policy has other open claims. Review them before
              adjusting utilization.
            </Alert>
          ) : null}

          <div className={classes.columns}>
            <div className={classes.column}>
              <div className={classes.adjustmentRow}>
                <TextField
                  variant='outlined'
                  type='number'
                  label='Deductible Adjustment'
                  inputProps={{ min: 0 }}
                  value={deductibleAdjustment}
                  onChange={handleDeductibleAdjustmentChange}
                  error={!!deductibleError}
                  helperText={deductibleError}
                />
                <TextField
                  variant='outlined'
                  label='Recalculated Deductible'
                  value={
                    calculationResult
                      ? formatRecalculated(
                          calculationResult.recalculatedDeductible,
                          data.policy_total_deductible
                        )
                      : ''
                  }
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </div>

              <div className={classes.adjustmentRow}>
                <TextField
                  variant='outlined'
                  type='number'
                  label='Annual Limit Adjustment'
                  inputProps={{ min: 0 }}
                  value={annualLimitAdjustment}
                  onChange={handleAnnualLimitAdjustmentChange}
                  error={!!annualLimitError}
                  helperText={annualLimitError}
                />
                <TextField
                  variant='outlined'
                  label='Recalculated Annual Limit'
                  value={
                    calculationResult
                      ? formatRecalculated(
                          calculationResult.recalculatedAnnualLimit,
                          data.policy_total_annual_limit
                        )
                      : ''
                  }
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </div>

              <div className={classes.adjustmentRow}>
                <Button
                  variant='outlined'
                  disabled={!isCalculateEnabled}
                  onClick={handleCalculate}
                >
                  Calculate
                </Button>
                <Button
                  variant='contained'
                  disabled={!isApplyEnabled}
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </div>
            </div>

            <div className={classes.column}>
              <div>
                <Typography variant='subtitle1' className={classes.subtitle}>
                  Current Policy Utilization
                </Typography>

                <Typography variant='body2' className={classes.bar}>
                  Deductible{' '}
                  <span>
                    {formatCurrency(data.policy_current_deductible, {
                      areCents: true,
                      hideZero: true,
                    })}{' '}
                    of{' '}
                    {formatCurrency(data.policy_total_deductible, {
                      areCents: true,
                      hideZero: true,
                    })}
                  </span>
                </Typography>

                <Typography variant='body2' className={classes.bar}>
                  Annual Limit{' '}
                  <span>
                    {formatCurrency(data.policy_current_annual_limit, {
                      areCents: true,
                      hideZero: true,
                    })}{' '}
                    of{' '}
                    {data.policy_total_annual_limit === -1
                      ? 'Unlimited'
                      : formatCurrency(data.policy_total_annual_limit, {
                          areCents: true,
                          hideZero: true,
                        })}
                  </span>
                </Typography>

                <Typography variant='body2' className={classes.bar}>
                  Reimbursement <span>{data.policy_reimbursement_percentage}%</span>
                </Typography>
              </div>

              <div>
                <Typography variant='subtitle1' className={classes.subtitle}>
                  Claim Utilization
                </Typography>

                <Typography variant='body2' className={classes.bar}>
                  Total Applied to Deductible{' '}
                  <span>
                    {formatCurrency(data.consumed_deductible, {
                      areCents: true,
                      hideZero: true,
                    })}
                  </span>
                </Typography>

                <Typography variant='body2' className={classes.bar}>
                  Total Paid{' '}
                  <span>
                    {formatCurrency(data.consumed_annual_limit, {
                      areCents: true,
                      hideZero: true,
                    })}
                  </span>
                </Typography>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <AdjustPolicyUtilizationConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmAdjustment}
        claimId={submittedClaimId}
        changes={adjustmentChanges}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

AdjustPolicyUtilization.propTypes = {
  petId: PropTypes.string,
};

export default AdjustPolicyUtilization;
